import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { ITask } from '@nimbus-assistant/models/task.model';
import { CodeInfo } from '@nimbus-assistant/models/task-types.model';
import { TaskAttributes } from '@nimbus-assistant/models/task-attributes.model';
import { isCodesEditingDisabled, isUnifyTask } from '@nimbus-assistant/helpers/task-priority.helpers';
import { localStorageKeys, LocalStorageService } from '@nimbus-assistant/services/local-storage.service';
import { AssistantTasksService } from '@nimbus-assistant/services/assistant-tasks.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { PluralForm } from '@nimbus-library/features/localization/models/enums/pluralform.enum';
import { LocalizationUIModule } from '@nimbus-library/features/localization/localization.module';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { INimbusFormGroup, NimbusFormGroupBuilder } from '@nimbus-library/features/shared/angular/forms';
import { ConversationOriginType } from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';

export interface ITaskCodesForm {
  primaryCode: string;
  secondaryCode: string;
  tags: string[];
  primaryCodeList: CodeInfo[];
  secondaryCodeList: CodeInfo[];
}

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-task-codes-tab',
  templateUrl: './task-codes-tab.component.html',
  styleUrls: ['./task-codes-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, SharedUIModule, LocalizationUIModule],
})
export class TaskCodesTabComponent implements OnInit, OnChanges {
  @Input() public task: ITask;

  public readonly TranslationKeys = TranslationKeys;
  public readonly PluralForm = PluralForm;
  public form$ = new BehaviorSubject<INimbusFormGroup<ITaskCodesForm>>(null);

  /** NimbusOne owns the codes and tags of a Unify call; the Assistant only reads them. */
  public get isManagedInNimbusOne(): boolean {
    return isUnifyTask(this.task);
  }

  public constructor(
    private readonly formBuilder: NimbusFormGroupBuilder,
    private readonly localStorageService: LocalStorageService,
    private readonly tasksService: AssistantTasksService,
  ) {}

  public ngOnInit(): void {
    // The tab is created when it is opened, so this picks up whatever NimbusOne wrote since the
    // codes were fetched for this leg. Only Unify calls need it - elsewhere the Assistant is the writer.
    if (this.isManagedInNimbusOne) {
      this.tasksService.refreshCodes(this.task);
    }
  }

  public ngOnChanges(): void {
    this.initForm();
  }

  private initForm(): void {
    if (!this.task?.conversationCodes) {
      this.form$.next(null);
      return;
    }

    // A persisted draft must not hide the values NimbusOne wrote for a Unify call.
    const storedAttributes = this.isManagedInNimbusOne ? null : this.getStoredCallAttributes(this.task.conversationId);

    const wrapper = this.formBuilder.group<ITaskCodesForm>({
      primaryCode: [storedAttributes?.primaryCode || this.task.conversationCodes.currentPrimaryCode],
      secondaryCode: [storedAttributes?.secondaryCode || this.task.conversationCodes.currentSecondaryCode],
      tags: [storedAttributes?.tags || this.task.conversationCodes.tags || []],
      primaryCodeList: [this.task.conversationCodes.primaryCodeList],
      secondaryCodeList: [this.task.conversationCodes.secondaryCodeList],
    });
    const form = wrapper.form;

    const isTransferOnUserExit = this.task.originType === ConversationOriginType.TransferOnUserExit;
    if (isTransferOnUserExit || isCodesEditingDisabled(this.task)) {
      form.get('primaryCode').disable({ emitEvent: false });
      form.get('secondaryCode').disable({ emitEvent: false });
      form.get('tags').disable({ emitEvent: false });
    }

    form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateStoredCallAttributes({
        conversationId: this.task.conversationId,
        unifiedConversationId: this.task.unifiedConversationId,
        userSessionId: this.task.userSessionId,
        primaryCode: form.get('primaryCode').value,
        secondaryCode: form.get('secondaryCode').value,
        tags: form
          .get('tags')
          .value.map((t: string) => t.trim())
          .filter((t: string) => !!t),
      });
    });

    this.form$.next(form);
  }

  private updateStoredCallAttributes(callAttributes: TaskAttributes): void {
    // Written as a complete entry rather than merged into whatever is there: a leftover entry from
    // another conversation must never contribute fields to this one.
    this.localStorageService.saveData(localStorageKeys.callAttributes, { ...callAttributes });
  }

  private getStoredCallAttributes(conversationId: string): TaskAttributes {
    const callAttributes: TaskAttributes = this.localStorageService.getData(localStorageKeys.callAttributes);
    return callAttributes?.conversationId === conversationId ? callAttributes : null;
  }
}
